import { backendApi } from "@/services/backend/api";
import { Confirmation } from "@/services/backend/htz/confirmations/types/confirmation";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { Lst } from "@/shared/lib/lst/lst";
import { Reason } from "@bitory-gmbh/nidavellir";
import { Address } from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import { ConfirmationBasePatchRequest } from "@/services/backend/documents/types/confirmation";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const htzConfirmationApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["HtzConfirmation"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      htzConfirmationPut: build.mutation<Confirmation, PutRequest>({
        query: (request) => ({
          url: `/htz/confirmation`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzConfirmation", id: request.id },
          "HtzConfirmation",
        ],
      }),
      htzConfirmationPatch: build.mutation<Confirmation, PatchRequest>({
        query: (request) => ({
          url: `/htz/confirmation`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzConfirmation", id: request.id },
          "HtzConfirmation",
        ],
      }),
      htzConfirmationShow: build.query<Confirmation, ShowRequest>({
        query: (request) => ({
          url: `/htz/confirmation`,
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("HtzConfirmation"),
      }),
      htzConfirmationList: build.query<Lst<Confirmation>, ListRequest>({
        query: (request) => ({
          url: `/htz/confirmation/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) =>
          providesList(result?.items, "HtzConfirmation"),
      }),
      htzConfirmationSetImmutable: build.mutation<
        Confirmation,
        SetImmutableRequest
      >({
        query: (request) => ({
          url: `/htz/confirmation/set-immutable`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_r, _e, request) => [
          { type: "HtzConfirmation", id: request.id },
          "HtzConfirmation",
          "NumbersDocumentNumber",
        ],
      }),
      htzConfirmationDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: `/htz/confirmation`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: ["HtzConfirmation"],
      }),
      htzConfirmationPdfPreview: build.query<
        { objectURL: string },
        PreviewPDFRequest
      >({
        query: (request) => ({
          url: `/htz/confirmation`,
          method: "GET",
          params: request,
          headers: { Accept: "application/pdf" },
          responseHandler: previewResponseHandler,
          // IMPORTANT. Since this query will download the PDF under no circumstances
          // should the results be cached. This could end up using the users memory.
          cache: "no-cache",
        }),
        providesTags: (_r, _e, request) => [
          { type: "HtzConfirmation", id: request.confirmationId },
        ],
      }),
      htzConfirmationPdfDownload: build.mutation<
        { filename: string; objectURL: string },
        PDFRequest
      >({
        query: (request) => ({
          url: `/htz/confirmation`,
          method: "GET",
          params: { ...request, download: true },
          headers: { Accept: "application/pdf" },
          responseHandler: downloadResponseHandler,
          // IMPORTANT. Since this query will download the PDF under no circumstances
          // should the results be cached. This could end up using the users memory.
          cache: "no-cache",
        }),
        invalidatesTags: (_r, _e, request) => [
          { type: "HtzConfirmation", id: request.confirmationId },
        ],
      }),
    }),
  });

export const {
  useHtzConfirmationPutMutation,
  useHtzConfirmationPatchMutation,
  useHtzConfirmationShowQuery,
  useHtzConfirmationListQuery,
  useHtzConfirmationSetImmutableMutation,
  useHtzConfirmationDeleteMutation,
  useHtzConfirmationPdfPreviewQuery,
  useHtzConfirmationPdfDownloadMutation,
} = htzConfirmationApi;

export type PutRequest = Confirmation;

export type PatchRequest = {
  id: string;
  specialAgreements?: string;
  internalComment?: string;
  serviceAddress?: Address | null;
  serviceContact?: Contact | null;
} & ConfirmationBasePatchRequest;

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<string>[];
  date?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

export interface DeleteRequest {
  id: string;
}

export enum ConfirmationDocument {
  ALL = "all",
  CONFIRMATION = "confirmation",
}

export interface SetImmutableRequest {
  id: string;
  reason: Reason;
  setBy: string;
}

export enum ConfirmationImmutabilityReason {
  SetByUser = "set_by_user",
}

export interface PreviewPDFRequest {
  confirmationId: string;
  document: ConfirmationDocument;
}

export interface PDFRequest {
  confirmationId: string;
  document: ConfirmationDocument;
  download: boolean;
  downloadBy: string;
}

/**
 * Patches the cached confirmation for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateHtzConfirmationCache = updateCache(
  htzConfirmationApi,
  "htzConfirmationShow",
);
